import type { CSSProperties, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { AppSidebar } from './AppSidebar';
import { BackToTopButton } from './BackToTopButton';
import { CheckBatchMinibar } from './CheckBatchMinibar';

/**
 * AppShell — R164 大改版全站統一 layout shell(側邊欄制,TopNav 退場)。
 * -----------------------------------------------------------------------------
 * 對齊 D3 2026-07-29 結構性改版(`專案_工作區.html` / `成效報表.html` 共用骨架):
 *
 *   ┌──────┬─────────────────────────────────────┐
 *   │ Side │  main content (children)            │
 *   │ bar  │                                     │
 *   │(64/  │                                     │
 *   │ 216) │                                     │
 *   └──────┴─────────────────────────────────────┘
 *
 * 舊 TopNav(56px 頂列:logo + GroupSwitcher + 用量 chip + Bell + ThemeMenu)
 * 整列退場:機能全數移入 AppSidebar(logo 品牌區 / 工作區切換器 / 帳號選單),
 * 用量 chip 隨「前端不顯示點數」拍板刪除、Bell 無新版對應刪除。
 * 各頁頂欄改 page-specific(如畫布頁 CanvasHeaderBar)。
 *
 * Sidebar 預設(沿用 Bryan 拍板 + D3 localStorage persist):
 *   - localStorage `hai.sidebarCollapsed` 存過 → 以存值為準
 *   - 沒存過:`/home` `/` 展開,其他頁收合;`initialSidebarCollapsed` 可顯式 override
 */

export interface AppShellProps {
  /**
   * Sidebar 初始 collapsed state(localStorage 沒存值時才生效)。
   *   - undefined: 自動依 pathname 判斷(`/home` 或 `/` 展開,其他收合)
   *   - true / false: caller 顯式 override
   */
  initialSidebarCollapsed?: boolean;
  /** 整體背景色 — 預設頁面底色語意 token(`--bg-base`;深色自動跟著翻)。 */
  background?: string;
  /** 主內容區(`<main>` slot)。AppShell 不額外包 `<main>` 標籤,caller 自決定 semantic。 */
  children: ReactNode;
  /**
   * Main 區自定 padding(預設 `28px 40px 56px`)。Wizard / editor 等需要 zero padding
   * 自己處理 content padding 時傳 `'0'`。
   */
  mainPadding?: CSSProperties['padding'];
  /**
   * Main 區是否撐滿 viewport(canvas / editor 等需要 child `flex: 1` 撐滿高度)。
   *   - `false`(default)— main slot 是 `overflowY: 'auto'`,適合 dashboard / list / form
   *   - `true` — main slot 變 flex column container 不 scroll(CoreCanvas + Inspector
   *     的 wrapper 能 stretch fill height)
   */
  mainFillHeight?: boolean;
}

export function AppShell({
  initialSidebarCollapsed,
  background = 'var(--bg-base)',
  children,
  mainPadding = '28px 40px 56px',
  mainFillHeight = false,
}: AppShellProps) {
  const { pathname } = useLocation();
  const isHome = pathname === '/home' || pathname === '/';

  // Pathname-derived default:home → expanded;其他 → collapsed
  const resolvedInitialCollapsed = initialSidebarCollapsed ?? !isHome;

  return (
    <div
      data-testid="app-shell"
      /* R218 — 這裡原本寫死 `data-theme="light"`。CSS 變數就近繼承 → 只要 App 內任何
       * 後代都會拿到 `.theme-light` block 的值,`<html data-theme="dark">` 永遠贏不了
       * (未包 AppShell 的 LoginPage 反而會變深色 = 半黑半白)。語意別名已於同輪搬進
       * `:root`(淺色即預設),這個 attribute 不再需要,拔掉深色模式才生效。
       * ⚠️ 不要加回來 —— 主題只由 `<html data-theme>` 一處決定(themeMode.ts)。 */
      style={{
        // mainFillHeight 頁(canvas / editor):**固定** 100vh(definite height)讓 flex 鏈有
        // 確定高可分配 → main 的 overflow:hidden 真的夾住、內層(如 Inspector aside)靠自身
        // overflow-y 內捲,不會被高內容往上撐高。一般 scrolling 頁維持 minHeight:100vh。
        ...(mainFillHeight ? { height: '100vh' } : { minHeight: '100vh' }),
        display: 'flex',
        background,
        color: 'var(--text-primary, var(--ink-100))',
      }}
    >
      <AppSidebar initialCollapsed={resolvedInitialCollapsed} />
      <div
        style={{
          flex: 1,
          padding: mainPadding,
          minWidth: 0,
          ...(mainFillHeight
            ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : { overflowY: 'auto' }),
        }}
      >
        {children}
      </div>
      {/* R209 — 跨頁批次檢查常駐條(右下角;無 batch 即 null,不影響任何頁)。 */}
      <CheckBatchMinibar />
      {/* 回到頂部(右下角;沒捲過門檻即 null,canvas 這種不捲 window 的頁永遠不出現)。 */}
      <BackToTopButton />
    </div>
  );
}
